import {
  SUCCESS,
  FETCH_SHIPMENT,
  FETCH_SHIPMENT_EVENTS,
  FETCH_SHIPMENT_LOCATIONS,
  FETCH_SHIPMENT_AND_LOCATION_STARTED,
  FETCH_SHIPMENT_AND_LOCATION_SUCCEEDED,
  STOP_SHIPMENT,
  CLEAR_SHIPMENT,
  FETCH_SHIPMENT_FAILED,
  FETCH_SHIPMENT_METRICS_BY_IDENTIFIER,
  FETCH_EVENTS_AND_LOGS_STARTED,
  FETCH_SHIPMENT_METRICS_BY_IDENTIFIER_STARTED,
  FETCH_SHIPMENT_METRICS_BY_IDENTIFIER_COMPLETED,
  SET_SHIPMENT_DATA,
  UPDATE_SHIPMENT_EVENTS
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { fetchAdditions, error, snackBarError } from '#action/fetch';
import { fetchDeviceDetails } from '#action/warehouse/appliance/appliance-devices';
import { fetchDelivery } from './delivery';
import { history } from '#configureStore';
import { getRequestOptions, addParamsToUri } from '#helper';

const fetchShipment = id => async dispatch => {
  try {
    const shipmentUrl = webPortalApi.buildApiUrl(`shipment/${id}`);
    const res = await fetch(shipmentUrl, getRequestOptions());
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }

    if (!res.ok) {
      const response = await res.json();
      const isShipmentNotFound = response.resStatus >= 400;
      dispatch(
        snackBarError({
          message: 'Error in request',
          ...response
        })
      );

      if (isShipmentNotFound) {
        await history.push('/shipment');
      }
      throw response;
    }
    const shipment = await res.json();

    const deviceIds = shipment.devices.map(d => d.id);
    if (deviceIds.length) {
      const deviceDetails = await fetchDeviceDetails(deviceIds);
      shipment.devices = shipment.devices.map(d => ({ ...d, ...deviceDetails[d.id] }));
    }

    if (shipment.projectId) {
      const projectRes = await fetch(webPortalApi.buildApiUrl(`project?modules=true&ids=${shipment.projectId}`), getRequestOptions());
      const [project] = await projectRes.json();
      shipment.project = project;
    }

    dispatch({
      type: FETCH_SHIPMENT,
      payload: shipment
    });
  } catch (e) {
    dispatch({
      type: FETCH_SHIPMENT_FAILED,
      error: e
    });
  }
};

export const setShipmentData = shipmentData => dispatch => {
  dispatch({ type: SET_SHIPMENT_DATA, payload: shipmentData });
};

export const fetchShipmentAndDelivery = id => dispatch =>
  Promise.all([
    dispatch(fetchShipment(id)),
    dispatch(fetchDelivery(id))
  ]);

function getUriShipmentMeasurements(id, period, path, queryParams) {
  let URI = `shipment/${id}/${path}`;

  if (period) {
    URI += `/${period.start}/${period.end}`;
  }

  if (queryParams) {
    URI += `/?${new URLSearchParams(queryParams)}`;
  }

  return URI;
}

const getUriShipmentParams = (id, period, path, params) => {
  const uri = getUriShipmentMeasurements(id, period, path);
  return addParamsToUri(uri, params);
};

export const fetchLocations = (id, period, withoutMetrics = false) => dispatch =>
  dispatch(fetchAdditions(
    getUriShipmentParams(id, period, 'locations', { withoutMetrics }),
    FETCH_SHIPMENT_LOCATIONS
  ));

export const fetchMetricsByIdentifier = (id, period, identifier, params) =>
  dispatch => {
    dispatch({
      type: FETCH_SHIPMENT_METRICS_BY_IDENTIFIER_STARTED,
      payload: identifier
    });
    return dispatch(fetchAdditions(
      getUriShipmentParams(id, period, 'metrics', { metric: identifier, ...params }),
      FETCH_SHIPMENT_METRICS_BY_IDENTIFIER,
      { identifier }
    )).then(() => {
      dispatch({
        type: FETCH_SHIPMENT_METRICS_BY_IDENTIFIER_COMPLETED,
        payload: identifier
      });
    });
  };

export const fetchShipmentAndLocation = id => async dispatch => {
  try {
    const shipmentURI = webPortalApi.buildApiUrl(`shipment/${id}`);
    const locationsURI = webPortalApi.buildApiUrl(getUriShipmentParams(id, '', 'locations', false));

    dispatch({ type: FETCH_SHIPMENT_AND_LOCATION_STARTED });

    const [shipmentRes, locationsRes] = await Promise.all([
      fetch(shipmentURI, getRequestOptions()),
      fetch(locationsURI, getRequestOptions())
    ]);

    const [shipmentData, locationsData] = await Promise.all([
      shipmentRes.json(),
      locationsRes.json()
    ]);

    dispatch({
      type: FETCH_SHIPMENT,
      payload: shipmentData
    });

    dispatch({
      type: FETCH_SHIPMENT_LOCATIONS,
      payload: locationsData
    });

    await dispatch(fetchDelivery(id));

    dispatch({ type: FETCH_SHIPMENT_AND_LOCATION_SUCCEEDED });
  } catch (err) {
    dispatch(
      error({
        message: 'Error in request'
      })
    );
  }
};

export const fetchEventsAndLogs = shipmentId => async dispatch => {
  try {
    const shipmentLogsURL = webPortalApi.buildApiUrl(getUriShipmentMeasurements(shipmentId, '', 'logs', { order: 'ASC' }));
    const shipmentEventsURL = webPortalApi.buildApiUrl(getUriShipmentMeasurements(shipmentId, '', 'events'));

    dispatch({ type: FETCH_EVENTS_AND_LOGS_STARTED });

    const [logsRes, eventsRes] = await Promise.all([
      fetch(shipmentLogsURL, getRequestOptions()),
      fetch(shipmentEventsURL, getRequestOptions())
    ]);

    const [logsData, eventsData] = await Promise.all([
      logsRes.json(),
      eventsRes.json()
    ]);

    return dispatch({
      type: FETCH_SHIPMENT_EVENTS,
      payload: {
        events: eventsData,
        logs: logsData?.logs ?? []
      }
    });
  } catch (err) {
    return dispatch(
      snackBarError({
        message: 'Error in request'
      })
    );
  }
};

export const deleteShipment = shipmentId =>
  dispatch =>
    fetch(webPortalApi.buildApiUrl(`shipment/${shipmentId}`), {
      method: 'DELETE',
      ...getRequestOptions()
    }).then(r => r.json().then(d => {
      if (!r.ok) {
        return dispatch(
          snackBarError({
            message: 'HTTP error occurred',
            ...d
          })
        );
      }

      return dispatch({
        type: SUCCESS,
        payload: {
          message: 'Shipment was deleted!'
        }
      });
    }));

export const stopShipment = (id, stopDate) =>
  dispatch =>
    fetch(webPortalApi.buildApiUrl(`shipment/${id}/stop`), {
      method: 'PATCH',
      ...getRequestOptions(),
      body: JSON.stringify(stopDate)
    }).then(r => r.json().then(d => {
      if (!r.ok) {
        return dispatch(
          snackBarError({
            message: 'Error in request',
            ...d
          })
        );
      }

      dispatch({
        type: STOP_SHIPMENT,
        payload: d
      });

      return dispatch({
        type: SUCCESS,
        payload: {
          message: 'Shipment was stopped!'
        }
      });
    }));

export const updateShipmentEvents = data => dispatch => {
  dispatch({ type: UPDATE_SHIPMENT_EVENTS, payload: data });
};

export const clearShipment = () =>
  dispatch => dispatch({ type: CLEAR_SHIPMENT });
